import { useState, useCallback } from 'react'

type OptimisticState<T> =
  { hasOptimisticValue: false } | { hasOptimisticValue: true; value: T | null }

/**
 * Custom hook for managing optimistic state updates
 *
 * @param serverValue - The current value from the server (source of truth)
 * @returns { currentValue: T | null, updateOptimistically: (newValue: unknown, asyncOperation: () => Promise<void>) => Promise<void>, clearOptimistic: () => void, isOptimistic: boolean }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useOptimisticState<T = any>(serverValue: T | null) {
  const [optimisticState, setOptimisticState] = useState<OptimisticState<T>>({
    hasOptimisticValue: false,
  })

  // Current value is optimistic value if set, otherwise server value
  const currentValue = optimisticState.hasOptimisticValue
    ? optimisticState.value
    : serverValue

  const updateOptimistically = useCallback(
    async (newValue: unknown, asyncOperation: () => Promise<void>) => {
      // Set optimistic value immediately for instant UI feedback
      setOptimisticState({
        hasOptimisticValue: true,
        value: newValue as T | null,
      })

      try {
        await asyncOperation()

        // Clear optimistic state - now using server data
        setOptimisticState({ hasOptimisticValue: false })
      } catch (error) {
        // Revert optimistic state on error
        console.error('Optimistic update failed:', error)
        setOptimisticState({ hasOptimisticValue: false })
        throw error
      }
    },
    []
  )

  // Function to clear optimistic state (manual reset)
  const clearOptimistic = useCallback(() => {
    setOptimisticState({ hasOptimisticValue: false })
  }, [])

  return {
    currentValue,
    updateOptimistically,
    clearOptimistic,
    isOptimistic: optimisticState.hasOptimisticValue,
  }
}
